import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { User } from '../contexts/AuthContext';

interface SupervisorDashboardProps {
  user: User;
}

interface SupervisorStats {
  auditoriasPendientes: number;
  correccionesHechas: number;
  auditoriasDelMes: any[];
  rendimientoPersonal: {
    porcentaje: number;
    objetivo: number;
  };
}

const SupervisorDashboard: React.FC<SupervisorDashboardProps> = ({ user }) => {
  const [stats, setStats] = useState<SupervisorStats | null>(null);
  const [auditorias, setAuditorias] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSupervisorData();
  }, []);

  const loadSupervisorData = async () => {
    try {
      setLoading(true);

      // Cargar auditorías pendientes de revisión del supervisor
      const auditoriasResponse = await api.get('/audits/supervisor/pending');

      const auditoriasData = auditoriasResponse.data.success ?
        (auditoriasResponse.data.auditorias || auditoriasResponse.data.data || []) : [];

      // Cargar todas las auditorías donde participó el supervisor (auditorías propias)
      const misAuditoriasResponse = await api.get('/audits/supervisor/my-audits');
      const misAuditorias = misAuditoriasResponse.data.success ? misAuditoriasResponse.data.data || [] : [];

      // Cargar auditorías completadas que fueron revisadas por el supervisor (correcciones)
      const auditoriasCompletadasResponse = await api.get('/audits?estado=entregada&supervisor=true');
      const auditoriasRevisadas = auditoriasCompletadasResponse.data.success ? auditoriasCompletadasResponse.data.data || [] : [];

      // Filtrar auditorías de este mes
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      // Auditorías propias de este mes
      const misAuditoriasDelMes = misAuditorias.filter((audit: any) => {
        const auditDate = new Date(audit.updatedAt || audit.createdAt);
        return auditDate.getMonth() === currentMonth && auditDate.getFullYear() === currentYear;
      });

      // Revisiones (correcciones) de este mes
      const revisionesDelMes = auditoriasRevisadas.filter((audit: any) => {
        const auditDate = new Date(audit.updatedAt || audit.createdAt);
        return auditDate.getMonth() === currentMonth &&
               auditDate.getFullYear() === currentYear &&
               !misAuditorias.find(ma => ma.id === audit.id); // Evitar duplicados
      });

      // Combinar todo el trabajo del mes
      const todoElTrabajoDelMes = [...misAuditoriasDelMes, ...revisionesDelMes];

      const statsData: SupervisorStats = {
        auditoriasPendientes: auditoriasData.length,
        correccionesHechas: todoElTrabajoDelMes.length,
        auditoriasDelMes: todoElTrabajoDelMes.slice(0, 10),
        rendimientoPersonal: {
          porcentaje: todoElTrabajoDelMes.length > 0 ?
            Math.round(Math.min((todoElTrabajoDelMes.length / 50) * 100, 100)) : 0,
          objetivo: 50
        }
      };


      setStats(statsData);
      setAuditorias(auditoriasData);

    } catch (error) {
      console.error('Error cargando datos del supervisor:', error);
      // Fallback con datos vacíos
      setStats({
        auditoriasPendientes: 0,
        correccionesHechas: 0,
        auditoriasDelMes: [],
        rendimientoPersonal: { porcentaje: 0, objetivo: 50 }
      });
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado: string) => {
    const colores = {
      'pendiente': '#f59e0b',
      'en_proceso': '#10b981',
      'pausada': '#ef4444',
      'terminada': '#8b5cf6',
      'entregada': '#059669'
    };
    return colores[estado as keyof typeof colores] || '#6b7280';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" />
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>
          Cargando tu panel de supervisor...
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ 
          fontSize: '1.875rem', 
          fontWeight: 'bold', 
          color: '#1f2937', 
          margin: '0 0 0.5rem 0' 
        }}>
          👨‍💼 Panel de Supervisor
        </h1>
        <p style={{ color: '#6b7280', margin: 0 }}>
          Vista de tus correcciones y auditorías pendientes de revisión
        </p>
      </div>

      {/* Métricas personales */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1.5rem',
        marginBottom: '2rem' 
      }}>
        <div className="metric-card">
          <div className="metric-icon">📋</div>
          <div className="metric-content">
            <h3>Auditorías Pendientes</h3>
            <div className="metric-number">
              {stats?.auditoriasPendientes || 0}
            </div>
            <p>Para revisar y corregir</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">✅</div>
          <div className="metric-content">
            <h3>Trabajo Completado</h3>
            <div className="metric-number">
              {stats?.correccionesHechas || 0}
            </div>
            <p>Auditorías propias + Correcciones este mes</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <h3>Tu Rendimiento</h3>
            <div className="metric-number" style={{ 
              color: (stats?.rendimientoPersonal.porcentaje || 0) >= 70 ? '#10b981' : '#f59e0b' 
            }}>
              {(stats?.rendimientoPersonal.porcentaje || 0).toFixed(1)}%
            </div>
            <p>Del objetivo personal</p>
          </div>
        </div>
      </div>

      {/* Auditorías pendientes de revisión - OCULTADO POR PEDIDO DEL USUARIO */}

      {/* Estilos */}
      <style jsx>{`
        .metric-card {
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .metric-icon {
          font-size: 2.5rem;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f3f4f6;
          border-radius: 12px;
        }

        .metric-content h3 {
          margin: 0 0 0.5rem 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .metric-number {
          font-size: 2rem;
          font-weight: bold;
          color: #1f2937;
          margin: 0 0 0.25rem 0;
        }

        .metric-content p {
          margin: 0;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .section-card {
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          margin-bottom: 2rem;
        }

        .table-header {
          padding: 0.875rem;
          text-align: left;
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .table-cell {
          padding: 1rem 0.875rem;
          font-size: 0.875rem;
          color: #374151;
          border-bottom: 1px solid #f3f4f6;
        }

        .spinner {
          border: 3px solid #f3f4f6;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SupervisorDashboard;